import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from 'd3-scale-chromatic';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

// Current datetime-helper
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: '#222', border: '1px solid #555' }}>
      <Typography variant="caption" sx={{ color: '#ddd' }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}

// SERIES (para checkboxes/leyenda/gráfico)
  const seriesOptions = [
    // Maestro (analógicas)
    { key: "tempProcesoEntrada",  label: "Temp. Proceso Entrada" },
    { key: "tempProcesoSalida",   label: "Temp. Proceso Salida" },
    { key: "condicionActualEquipo", label: "Condición Actual del Equipo" },

    // Maestro (digitales: status de módulos)
    ...Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return { key: `modulo${n}Status`, label: `Módulo ${n} Status` };
    }),

    // Módulos 1..10 (principalmente analógicas)
    ...Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return [
        { key: `m${n}CondicionCompresor`, label: `M${n} Condición Actual del Compresor` },
        { key: `m${n}CargaCompresor`,     label: `M${n} % de Carga Compresor` },
        { key: `m${n}TempEvapSalida`,     label: `M${n} Temp Evap Salida` },
        { key: `m${n}TempDescarga`,       label: `M${n} Temp Descarga` },
        { key: `m${n}PresionSuccion`,     label: `M${n} Presión Succión` },
        { key: `m${n}PresionDescarga`,    label: `M${n} Presión Descarga` },
        { key: `m${n}IDE`,                label: `IDE M${n}` }, // quita esta línea si no quieres graficar IDE
      ];
    }).flat(),
  ];


const GFullServiceCooling = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  // Data state
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE  = "Full Service Cooling";
  const QUERY    = "Grafica";


  // toggle selection
  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
  // 1) validación de fechas
  if (new Date(fechaFinal) < new Date(fechaInicio)) {
    setError("La fecha final no puede ser anterior a la fecha de inicio.");
    return;
  }

  setError(null);
  setLoading(true);
  setRows([]);

  try {
    // 2) petición
    const res = await axios.post(
  `${API_BASE}/test/consulta/` +
    `${encodeURIComponent(MACHINE)}/` +
    `${encodeURIComponent(QUERY)}`,
  { idEquipo, fechaInicio, fechaFinal },
  { headers: { "Content-Type": "application/json" } }
  );


    // 3) parsear body
    const body = res.data.body
      ? (typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body)
      : res.data;

    //console.log("👉 body ya parseado:", JSON.stringify(body, null, 2));

    // 4) extraer array
    const extractedData = Array.isArray(body.data)
      ? body.data
      : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

    // 5) limpiar claves
    const normalizeKeys = row => {
      const clean = {};
      for (const k in row) clean[k.trim()] = row[k];
      return clean;
    };
    
    // 6) mapeo completo (copia aquí TODOS tus campos)
    const mapped = extractedData.map((row, i) => {
      const r = normalizeKeys(row);
      const fechaUTC = new Date(r["Fecha"]);
      const ts = fechaUTC.getTime();
      const formattedDate = 
        `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth()+1).padStart(2,"0")}`+
        `-${String(fechaUTC.getUTCDate()).padStart(2,"0")} `+
        `${String(fechaUTC.getUTCHours()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCMinutes()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCSeconds()).padStart(2,"0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: row["Tipo de Máquina y Control"],
          numeroEquipo: row["Número de Equipo"],
          fecha: formattedDate, // ✅ Se usa la fecha convertida aquí
          tempProcesoEntrada: row["Temp Proceso Entrada"],
            tempProcesoSalida: row["Temp Proceso Salida"],
            condicionActualEquipo: row["Condicion Actual del Equipo"],
            modulo1Status: row["Módulo 1 Status"],
            modulo2Status: row["Módulo 2 Status"],
            modulo3Status: row["Módulo 3 Status"],
            modulo4Status: row["Módulo 4 Status"],
            modulo5Status: row["Módulo 5 Status"],
            modulo6Status: row["Módulo 6 Status"],
            modulo7Status: row["Módulo 7 Status"],
            modulo8Status: row["Módulo 8 Status"],
            modulo9Status: row["Módulo 9 Status"],
            modulo10Status: row["Módulo 10 Status"],
            m1CondicionCompresor: row["M1 Condición Actual del Compresor"],
            m1CargaCompresor: row["M1 % de Carga Compresor"],
            m1TempEvapSalida: row["M1 Temp Evap Salida"],
            m1TempDescarga: row["M1 Temp Descarga"],
            m1PresionSuccion: row["M1 Presión Succión"],
            m1PresionDescarga: row["M1 Presión Descarga"],
            m1IDE: row["IDE M1"],
            m2CondicionCompresor: row["M2 Condición Actual del Compresor"],
            m2CargaCompresor: row["M2 % de Carga Compresor"],
            m2TempEvapSalida: row["M2 Temp Evap Salida"],
            m2TempDescarga: row["M2 Temp Descarga"],
            m2PresionSuccion: row["M2 Presión Succión"],
            m2PresionDescarga: row["M2 Presión Descarga"],
            m2IDE: row["IDE M2"],
            m3CondicionCompresor: row["M3 Condición Actual del Compresor"],
            m3CargaCompresor: row["M3 % de Carga Compresor"],
            m3TempEvapSalida: row["M3 Temp Evap Salida"],
            m3TempDescarga: row["M3 Temp Descarga"],
            m3PresionSuccion: row["M3 Presión Succión"],
            m3PresionDescarga: row["M3 Presión Descarga"],
            m3IDE: row["IDE M3"],
            m4CondicionCompresor: row["M4 Condición Actual del Compresor"],
            m4CargaCompresor: row["M4 % de Carga Compresor"],
            m4TempEvapSalida: row["M4 Temp Evap Salida"],
            m4TempDescarga: row["M4 Temp Descarga"],
            m4PresionSuccion: row["M4 Presión Succión"],
            m4PresionDescarga: row["M4 Presión Descarga"],
            m4IDE: row["IDE M4"],
            m5CondicionCompresor: row["M5 Condición Actual del Compresor"],
            m5CargaCompresor: row["M5 % de Carga Compresor"],
            m5TempEvapSalida: row["M5 Temp Evap Salida"],
            m5TempDescarga: row["M5 Temp Descarga"],
            m5PresionSuccion: row["M5 Presión Succión"],
            m5PresionDescarga: row["M5 Presión Descarga"],
            m5IDE: row["IDE M5"],
            m6CondicionCompresor: row["M6 Condición Actual del Compresor"],
            m6CargaCompresor: row["M6 % de Carga Compresor"],
            m6TempEvapSalida: row["M6 Temp Evap Salida"],
            m6TempDescarga: row["M6 Temp Descarga"],
            m6PresionSuccion: row["M6 Presión Succión"],
            m6PresionDescarga: row["M6 Presión Descarga"],
            m6IDE: row["IDE M6"],
            m7CondicionCompresor: row["M7 Condición Actual del Compresor"],
            m7CargaCompresor: row["M7 % de Carga Compresor"],
            m7TempEvapSalida: row["M7 Temp Evap Salida"],
            m7TempDescarga: row["M7 Temp Descarga"],
            m7PresionSuccion: row["M7 Presión Succión"],
            m7PresionDescarga: row["M7 Presión Descarga"],
            m7IDE: row["IDE M7"],
            m8CondicionCompresor: row["M8 Condición Actual del Compresor"],
            m8CargaCompresor: row["M8 % de Carga Compresor"],
            m8TempEvapSalida: row["M8 Temp Evap Salida"],
            m8TempDescarga: row["M8 Temp Descarga"],
            m8PresionSuccion: row["M8 Presión Succión"],
            m8PresionDescarga: row["M8 Presión Descarga"],
            m8IDE: row["IDE M8"],
            m9CondicionCompresor: row["M9 Condición Actual del Compresor"],
            m9CargaCompresor: row["M9 % de Carga Compresor"],
            m9TempEvapSalida: row["M9 Temp Evap Salida"],
            m9TempDescarga: row["M9 Temp Descarga"],
            m9PresionSuccion: row["M9 Presión Succión"],
            m9PresionDescarga: row["M9 Presión Descarga"],
            m9IDE: row["IDE M9"],
            m10CondicionCompresor: row["M10 Condición Actual del Compresor"],
            m10CargaCompresor: row["M10 % de Carga Compresor"],
            m10TempEvapSalida: row["M10 Temp Evap Salida"],
            m10TempDescarga: row["M10 Temp Descarga"],
            m10PresionSuccion: row["M10 Presión Succión"],
            m10PresionDescarga: row["M10 Presión Descarga"],
            m10IDE: row["IDE M10"],
          };

    // 🔌 OPCIONAL (pero recomendable si tus datos vienen como strings/ON/OFF):
     //Normaliza todo lo que no sea texto a número/0-1
     const parseValue = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const s = String(v).trim().toLowerCase();
       if (["true","on","encendido","sí","si","1"].includes(s)) return 1;
       if (["false","off","apagado","no","0"].includes(s)) return 0;
       const n = Number(s.replace(",", "."));
       return Number.isFinite(n) ? n : v;
    };
     const TEXT_KEYS = new Set(["id","tipoMaquinaControl","numeroEquipo","fecha","ts"]);
     Object.keys(base).forEach(k => {
       if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
     });

    return base;
      })

    // 6.1) ✅ Ordena una sola vez aquí
    mapped.sort((a, b) => {
      const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
      const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
      return ta - tb; // ascendente: del más viejo al más nuevo
    });

      setRows(mapped);

  } catch (err) {
    console.error(err);
    setError("Error al consultar los datos. Intenta de nuevo.");
  } finally {
    setLoading(false);
  }
};

// Lista de pestañas
  const tabList = [
  { id: "maestro", label: "Maestro" },
  { id: "mod1",    label: "Modulo 1" },
  { id: "mod2",    label: "Modulo 2" },
  { id: "mod3",    label: "Modulo 3" },
  { id: "mod4",    label: "Modulo 4" },
  { id: "mod5",    label: "Modulo 5" },
  { id: "mod6",    label: "Modulo 6" },
  { id: "mod7",    label: "Modulo 7" },
  { id: "mod8",    label: "Modulo 8" },
  { id: "mod9",    label: "Modulo 9" },
  { id: "mod10",   label: "Modulo 10" },
  ];

  // Map: pestaña -> keys que se muestran en esa pestaña
  const groupKeys = {
  maestro: [
    "tempProcesoEntrada",
    "tempProcesoSalida",
    "condicionActualEquipo",
    "modulo1Status",
    "modulo2Status",
    "modulo3Status",
    "modulo4Status",
    "modulo5Status",
    "modulo6Status",
    "modulo7Status",
    "modulo8Status",
    "modulo9Status",
    "modulo10Status",
  ],
  mod1:  [
    "m1CondicionCompresor",
    "m1CargaCompresor",
    "m1TempEvapSalida",
    "m1TempDescarga",
    "m1PresionSuccion",
    "m1PresionDescarga",
  ],
  mod2:  [
    "m2CondicionCompresor",
    "m2CargaCompresor",
    "m2TempEvapSalida",
    "m2TempDescarga",
    "m2PresionSuccion",
    "m2PresionDescarga",
  ],
  mod3:  [
    "m3CondicionCompresor",
    "m3CargaCompresor",
    "m3TempEvapSalida",
    "m3TempDescarga",
    "m3PresionSuccion",
    "m3PresionDescarga",
  ],
  mod4:  [
    "m4CondicionCompresor",
    "m4CargaCompresor",
    "m4TempEvapSalida",
    "m4TempDescarga",
    "m4PresionSuccion",
    "m4PresionDescarga",
  ],
  mod5:  [
    "m5CondicionCompresor",
    "m5CargaCompresor",
    "m5TempEvapSalida",
    "m5TempDescarga",
    "m5PresionSuccion",
    "m5PresionDescarga",
  ],
  mod6:  [
    "m6CondicionCompresor",
    "m6CargaCompresor",
    "m6TempEvapSalida",
    "m6TempDescarga",
    "m6PresionSuccion",
    "m6PresionDescarga",
  ],
  mod7:  [
    "m7CondicionCompresor",
    "m7CargaCompresor",
    "m7TempEvapSalida",
    "m7TempDescarga",
    "m7PresionSuccion",
    "m7PresionDescarga",
  ],
  mod8:  [
    "m8CondicionCompresor",
    "m8CargaCompresor",
    "m8TempEvapSalida",
    "m8TempDescarga",
    "m8PresionSuccion",
    "m8PresionDescarga",
  ],
  mod9:  [
    "m9CondicionCompresor",
    "m9CargaCompresor",
    "m9TempEvapSalida",
    "m9TempDescarga",
    "m9PresionSuccion",
    "m9PresionDescarga",
  ],
  mod10: [
    "m10CondicionCompresor",
    "m10CargaCompresor",
    "m10TempEvapSalida",
    "m10TempDescarga",
    "m10PresionSuccion",
    "m10PresionDescarga",
  ],
};



  // COLUMNAS (DataGrid)
  const columns = [
    { field: "id",                headerName: "ID",                            width: 90 },
    { field: "tipoMaquinaControl",headerName: "Tipo de Máquina y Control",     minWidth: 220, flex: 1 },
    { field: "numeroEquipo",      headerName: "Número de Equipo",              minWidth: 160, flex: 1 },
    { field: "fecha",             headerName: "Fecha",                         minWidth: 180, flex: 1 },

    // Maestro
    { field: "tempProcesoEntrada",   headerName: "Temp. Proceso Entrada",      minWidth: 200, flex: 1 },
    { field: "tempProcesoSalida",    headerName: "Temp. Proceso Salida",       minWidth: 200, flex: 1 },
    { field: "condicionActualEquipo",headerName: "Condición Actual del Equipo",minWidth: 230, flex: 1 },

    // Status de módulos (digitales)
    ...Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return {
        field: `modulo${n}Status`,
        headerName: `Módulo ${n} Status`,
        minWidth: 180,
        flex: 1,
      };
    }),

    // Por módulo 1..10
    ...Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return [
        { field: `m${n}CondicionCompresor`, headerName: `M${n} Condición Actual del Compresor`, minWidth: 260, flex: 1 },
        { field: `m${n}CargaCompresor`,     headerName: `M${n} % de Carga Compresor`,          minWidth: 220, flex: 1 },
        { field: `m${n}TempEvapSalida`,     headerName: `M${n} Temp Evap Salida`,              minWidth: 200, flex: 1 },
        { field: `m${n}TempDescarga`,       headerName: `M${n} Temp Descarga`,                 minWidth: 200, flex: 1 },
        { field: `m${n}PresionSuccion`,     headerName: `M${n} Presión Succión`,               minWidth: 210, flex: 1 },
        { field: `m${n}PresionDescarga`,    headerName: `M${n} Presión Descarga`,              minWidth: 210, flex: 1 },
        { field: `m${n}IDE`,                headerName: `IDE M${n}`,                           minWidth: 150, flex: 1 }, // opcional
      ];
    }).flat(),
  ];


    const dummyData = [
    { fecha: "1", value: 2 },
    { fecha: "2", value: 5.5 },
    { fecha: "3", value: 2 },
    { fecha: "5", value: 8.5 },
    { fecha: "8", value: 1.5 },
    { fecha: "10", value: 5 },
    ];


  // Keys a mostrar según la pestaña activa
  const activeTabId = tabList[tabIndex]?.id ?? "maestro";
  const keysInActiveTab = groupKeys[activeTabId] ?? [];

  // 1) Si tengo datos reales los uso, si no pongo un dummy
  const displayData = rows.length ? rows : dummyData;

  // 2) De mis opciones, cojo sólo las que estén marcadas
  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter(opt => selectedKeys.includes(opt.key))
      .map(opt => opt.key);

    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  // 4) Genero un color para cada serie
  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
  const m = Object.fromEntries(seriesOptions.map(o => [o.key, o.label]));
  m.value = "Valor (demo)";
  return m;
  }, []);


  // console.log("🔥 columns que llega a DataGrid:", columns);
    
     return (

       <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="Full Service Cooling"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      {/* Top Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Middle Row: Variables con Tabs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            {/* Tabs para Maestro / Módulos */}
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ mb: 1 }}
              >
                {tabList.map((t, i) => (
                  <Tab key={t.id} label={t.label} />
                ))}
              </Tabs>

              {/* Panel único que muestra las variables del grupo seleccionado */}
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => keysInActiveTab.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: 12, fontWeight: "bold" }}>
                              {opt.label}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))
                  }

                  {/* Mensaje si una pestaña no tiene variables mapeadas */}
                  {seriesOptions.filter(opt => keysInActiveTab.includes(opt.key)).length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        No hay variables configuradas para {tabList[tabIndex]?.label}.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

            {/* Botón Graficar */}
            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => { e.preventDefault(); fetchRows(); }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        {/* Table placeholder */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,       // la altura fija del Paper
              p: 2,
              display: "flex",   // IMPORTANTE → convierte al Paper en un contenedor flex
              flexDirection: "column"
            }}
          >

            {/*
              Aquí le cedemos todo el espacio restante al DataGrid
              envolviéndolo en un Box con flex: 1.
            */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Final Row: Full‑width Chart */}
      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{display:"flex",justifyContent:"center",alignItems:"center",height:"100%"}}>
                <CircularProgress/>
              </Box>
            ) : (
              <>
                <Box
                  sx={{ 
                    display: "flex", 
                    overflowX: "auto", 
                    whiteSpace: "nowrap", 
                    mb: 1,                // espacio con el chart 
                    ml: "30px",           // desplazamiento igual al margin.left del chart 
                    width: "calc(100% - 60px)", // 100% menos left + right margins (20+30) 
                  }} 
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}          
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds} 
                    />
                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {/* Ya no va <Legend> aquí */}

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>  
  );
};

export default GFullServiceCooling;